import { isAxiosError } from 'axios';

export type RecordingErrorKind =
  | 'not_found'
  | 'expired'
  | 'unavailable'
  | 'network'
  | 'timeout'
  | 'server'
  | 'playback';

export class RecordingFetchError extends Error {
  readonly kind: RecordingErrorKind;

  constructor(kind: RecordingErrorKind, message?: string) {
    super(message ?? recordingErrorCopy(kind).title);
    this.name = 'RecordingFetchError';
    this.kind = kind;
  }
}

export function recordingErrorCopy(kind: RecordingErrorKind): { title: string; detail: string } {
  switch (kind) {
    case 'not_found':
      return {
        title: 'Recording not found',
        detail:
          'No recording is saved for this call. It may not have been recorded, or it is still being processed.',
      };
    case 'expired':
      return {
        title: 'Recording link expired',
        detail:
          'The stored recording link is no longer valid. We tried to fetch a fresh copy from Telnyx but could not load it.',
      };
    case 'unavailable':
      return {
        title: 'Recording unavailable',
        detail:
          'The recording exists but could not be loaded right now. Please try again in a few minutes.',
      };
    case 'network':
      return {
        title: 'Connection problem',
        detail: 'Could not reach the server. Check your internet connection and try again.',
      };
    case 'timeout':
      return {
        title: 'Recording took too long',
        detail: 'Loading the recording timed out. Try again — large files may take a moment.',
      };
    case 'server':
      return {
        title: 'Server error',
        detail: 'Something went wrong on our side while loading the recording. Please try again later.',
      };
    case 'playback':
      return {
        title: 'Could not play recording',
        detail:
          'The file loaded but your browser could not play it. The recording may be corrupted or still processing.',
      };
    default:
      return {
        title: 'Recording not found',
        detail: 'Unable to load this recording.',
      };
  }
}

async function readErrorMessageFromResponse(data: unknown): Promise<string> {
  if (!data) return '';
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text.trim()) return '';
      const parsed = JSON.parse(text) as { error?: { message?: string }; message?: string };
      return parsed.error?.message ?? parsed.message ?? text;
    } catch {
      return '';
    }
  }
  if (typeof data === 'object' && data !== null) {
    const obj = data as { error?: { message?: string }; message?: string };
    return obj.error?.message ?? obj.message ?? '';
  }
  return typeof data === 'string' ? data : '';
}

export async function classifyRecordingFetchError(error: unknown): Promise<RecordingFetchError> {
  if (error instanceof RecordingFetchError) return error;

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const apiMessage = await readErrorMessageFromResponse(error.response?.data);
    const combined = `${apiMessage} ${error.message}`.toLowerCase();

    if (error.code === 'ECONNABORTED') {
      return new RecordingFetchError('timeout');
    }
    if (!error.response) {
      return new RecordingFetchError('network');
    }
    if (status === 404 || combined.includes('not found')) {
      return new RecordingFetchError('not_found');
    }
    if (
      status === 502 ||
      status === 403 ||
      combined.includes('expired') ||
      combined.includes('presigned') ||
      combined.includes('source returned 403') ||
      combined.includes('source returned 404')
    ) {
      return new RecordingFetchError('expired');
    }
    if (status && status >= 500) {
      return new RecordingFetchError('server');
    }
    return new RecordingFetchError('unavailable');
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('not found')) return new RecordingFetchError('not_found');
    if (msg.includes('empty')) return new RecordingFetchError('not_found');
  }

  return new RecordingFetchError('unavailable');
}
